import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Camera, Lock, MapPin, Timer, Zap, CheckCircle2 } from "lucide-react";
import { vakmanAanvragen } from "@/lib/vakman.functions";
import { VakmanPoort } from "@/components/vakman";
import { OPVOLG_OPTIES } from "@/lib/vakman-shared";

export const Route = createFileRoute("/vakman/")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Vakmanportaal | offerto" },
      { name: "description", content: "Bekijk offerteaanvragen voor elektriciens in jouw regio en ontgrendel de contactgegevens." },
      { property: "og:title", content: "Vakmanportaal | offerto" },
      { property: "og:description", content: "Offerteaanvragen voor elektriciens in jouw regio." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => <VakmanPoort>{() => <Lijst />}</VakmanPoort>,
});

function tijd(iso: string) {
  const d = new Date(iso);
  const vandaag = new Date().toDateString() === d.toDateString();
  return vandaag ? d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

function Lijst() {
  const [tab, setTab] = useState<"beschikbaar" | "ontgrendeld">("beschikbaar");
  const [zoek, setZoek] = useState("");
  const [vanaf, setVanaf] = useState("");
  const [tot, setTot] = useState("");
  const haal = useServerFn(vakmanAanvragen);
  const { data, isPending, isError } = useQuery({ queryKey: ["vakman-aanvragen", tab], queryFn: () => haal({ data: { tab } }) });
  const q = zoek.trim().toLowerCase();
  const rijen = (data ?? []).filter((r) => {
    const dag = r.created_at.slice(0, 10);
    if (vanaf && dag < vanaf) return false;
    if (tot && dag > tot) return false;
    return !q || [r.titel, r.plaats, r.naam ?? ""].some((s) => s.toLowerCase().includes(q));
  });

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="space-y-4 border-b border-border p-5">
        <h1 className="text-2xl font-bold">Offerteaanvragen</h1>
        <div className="flex gap-2">
          {(["beschikbaar", "ontgrendeld"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex items-center gap-1.5 rounded-lg border-2 px-4 py-2 font-bold ${tab === t ? "border-primary text-foreground" : "border-border text-muted-foreground"}`}>
              {t === "beschikbaar" ? <>Beschikbaar <Zap className="h-4 w-4" /></> : <>Ontgrendeld <CheckCircle2 className="h-4 w-4" /></>}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" aria-label="Datum vanaf" value={vanaf} onChange={(e) => setVanaf(e.target.value)} className="h-11 rounded-md border border-input px-3 text-base" />
          <input type="date" aria-label="Datum t/m" value={tot} onChange={(e) => setTot(e.target.value)} className="h-11 rounded-md border border-input px-3 text-base" />
        </div>
        <input placeholder="Zoek op klus, plaats of naam" value={zoek} onChange={(e) => setZoek(e.target.value)} className="h-11 w-full rounded-md border border-input px-3 text-base" />
      </div>
      {isPending && <p className="p-5 text-muted-foreground">Laden…</p>}
      {isError && <p className="p-5 text-destructive">Aanvragen laden mislukt.</p>}
      {!isPending && rijen.length === 0 && (
        <p className="p-5 text-muted-foreground">{tab === "beschikbaar" ? "Er zijn op dit moment geen nieuwe aanvragen in jouw werkgebied." : "Je hebt nog geen aanvragen ontgrendeld."}</p>
      )}
      <ul className="divide-y divide-border">
        {rijen.map((r) => (
          <li key={r.id}>
            <Link to="/vakman/aanvraag/$id" params={{ id: r.id }} className="block px-5 py-4 hover:bg-secondary/60">
              <div className="flex items-start justify-between gap-3">
                <p className="font-bold">
                  {r.titel}{r.soort ? ` – ${r.soort.toLowerCase()}` : ""} {tab === "beschikbaar" && <Lock className="ml-1 inline h-4 w-4" />}
                </p>
                <span className="shrink-0 text-sm font-bold text-primary">{tijd(r.created_at)}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{r.plaats}</span>
                {r.uitvoerdatum && <span className="flex items-center gap-1"><Timer className="h-4 w-4" />{r.uitvoerdatum}</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                {tab === "beschikbaar" && (
                  <span className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1 font-bold">
                    <Zap className="h-4 w-4" />{r.kopers === 0 ? "Ontgrendel als eerste" : `${r.kopers} concurrent${r.kopers === 1 ? "" : "en"}`}
                  </span>
                )}
                {r.aantalFotos > 0 && <span className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1"><Camera className="h-4 w-4" />{r.aantalFotos} foto{r.aantalFotos === 1 ? "" : "'s"}</span>}
                {tab === "ontgrendeld" && r.naam && <span className="rounded-md border border-border px-2.5 py-1">{r.naam}</span>}
                {tab === "ontgrendeld" && (
                  <span className="rounded-md bg-secondary px-2.5 py-1">{r.opvolgstatus ? OPVOLG_OPTIES[r.opvolgstatus as keyof typeof OPVOLG_OPTIES] : "Nog geen opvolging"}</span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
